import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { useSeo } from '../utils/useSeo';

type QuizStage = 'intro' | 'question' | 'insight' | 'analyzing' | 'community' | 'projection' | 'result';
type InsightType = 'sourcing' | 'photo' | null;
type QuizQuestionId =
  | 'identity'
  | 'dealSource'
  | 'searchMethod'
  | 'volume'
  | 'speed'
  | 'listings'
  | 'profitTracking'
  | 'bottleneck';

type QuizAnswerOption = {
  emoji?: string;
  id: string;
  label: string;
  score: number;
};

type QuizQuestion = {
  id: QuizQuestionId;
  optional?: boolean;
  options: QuizAnswerOption[];
  prompt: string;
};

type QuizAnswers = Partial<Record<QuizQuestionId, string>>;

type LeakageBand = 'low' | 'moderate' | 'high' | 'critical';

const MARKETPLACE_SOURCE_IDS = ['facebook-marketplace', 'website-marketplace'] as const;

const MARKETPLACE_FOLLOWUP_QUESTION: QuizQuestion = {
  id: 'searchMethod',
  options: [
    { id: 'manual', label: 'Manually (I browse and filter listings myself)', score: 2 },
    { id: 'saved-alerts', label: 'Saved searches / alerts', score: 1 },
    { id: 'ai-automation', label: 'AI automation tools', score: 0 },
    { id: 'mixed', label: 'Mixed approach', score: 1 },
  ],
  prompt: 'How do you search these marketplaces?',
};

const BASE_QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'identity',
    options: [
      { emoji: '🏢', id: 'dealership', label: 'I run a dealership (manage stock)', score: 0 },
      { emoji: '🔁', id: 'flipper', label: 'I flip cars for profit', score: 0 },
      { emoji: '⚙️', id: 'both', label: 'I do both', score: 0 },
    ],
    prompt: 'What best describes your business?',
  },
  {
    id: 'dealSource',
    options: [
      { id: 'facebook-marketplace', label: 'Facebook marketplace', score: 1 },
      { id: 'website-marketplace', label: 'Website marketplace', score: 1 },
      { id: 'personal-network', label: 'Personal network', score: 1 },
    ],
    prompt: 'Where do you source your deals?',
  },
  {
    id: 'volume',
    options: [
      { id: '1-5', label: '1–5', score: 0 },
      { id: '5-15', label: '5–15', score: 0 },
      { id: '15-50', label: '15–50', score: 0 },
      { id: '50+', label: '50+', score: 0 },
    ],
    prompt: 'How many cars do you handle per month?',
  },
  {
    id: 'speed',
    options: [
      { id: 'same-day', label: 'Immediately (same day)', score: 0 },
      { id: '1-2-days', label: 'Within 1–2 days', score: 1 },
      { id: 'few-days', label: 'A few days later', score: 2 },
      { id: 'too-late', label: 'Often too late — I miss deals', score: 2 },
    ],
    prompt: 'How fast can you act on a good deal?',
  },
  {
    id: 'listings',
    options: [
      { id: 'quick-upload', label: 'Quick phone photos + upload', score: 2 },
      { id: 'basic-editing', label: 'Basic editing + manual process', score: 1 },
      { id: 'outsource', label: 'I outsource photos', score: 1 },
      { id: 'consistent-system', label: 'I have a consistent system', score: 0 },
    ],
    prompt: 'How do you prepare your car listings?',
  },
  {
    id: 'profitTracking',
    options: [
      { id: 'accurate', label: 'Yes — always accurate', score: 0 },
      { id: 'rough-estimate', label: 'Rough estimate', score: 1 },
      { id: 'not-really', label: 'Not really', score: 2 },
      { id: 'no-tracking', label: 'No, I don’t track it properly', score: 2 },
    ],
    prompt: 'Do you know your exact profit per car after all costs?',
  },
  {
    id: 'bottleneck',
    optional: true,
    options: [
      { id: 'finding-deals', label: 'Finding good deals', score: 2 },
      { id: 'selling-faster', label: 'Selling faster', score: 1 },
      { id: 'tracking-profit', label: 'Tracking profit', score: 2 },
      { id: 'managing-inventory', label: 'Managing inventory', score: 1 },
    ],
    prompt: 'What’s your biggest bottleneck right now?',
  },
];

const ALL_QUIZ_QUESTIONS: QuizQuestion[] = [...BASE_QUIZ_QUESTIONS, MARKETPLACE_FOLLOWUP_QUESTION];

function isMarketplaceSource(optionId?: string): boolean {
  if (!optionId) {
    return false;
  }
  return MARKETPLACE_SOURCE_IDS.includes(optionId as (typeof MARKETPLACE_SOURCE_IDS)[number]);
}

const VOLUME_TO_MONTHLY_CARS: Record<string, number> = {
  '1-5': 3,
  '5-15': 10,
  '15-50': 28,
  '50+': 60,
};

const ANALYZING_LINES = [
  'Checking sourcing efficiency...',
  'Evaluating listing quality...',
  'Calculating profit leaks...',
] as const;
const quizIntroImage = '/quizpage.png';
const aiBeforeImage = '/before-optimized.jpeg';
const aiAfterImage = '/after.jpeg';
const LISTINGS_INFO_TRIGGER_IDS = ['quick-upload', 'basic-editing', 'outsource'] as const;
const introSubtextItems = [
  'Identify where you’re overpaying for cars',
  'Spot gaps in your sourcing strategy',
  'See how your deal speed compares',
  'Estimate your missed monthly profit',
] as const;

const communityPins = [
  { left: '10%', src: 'https://randomuser.me/api/portraits/men/11.jpg', top: '24%' },
  { left: '30%', src: 'https://randomuser.me/api/portraits/women/22.jpg', top: '18%' },
  { left: '52%', src: 'https://randomuser.me/api/portraits/men/31.jpg', top: '28%' },
  { left: '67%', src: 'https://randomuser.me/api/portraits/women/37.jpg', top: '16%' },
  { left: '84%', src: 'https://randomuser.me/api/portraits/women/44.jpg', top: '22%' },
  { left: '37%', src: 'https://randomuser.me/api/portraits/men/58.jpg', top: '46%' },
  { left: '77%', src: 'https://randomuser.me/api/portraits/men/69.jpg', top: '52%' },
] as const;

const ctaClass =
  'h-12 w-full rounded-[10px] border border-black/65 bg-[#08090c] px-8 text-sm font-bold uppercase tracking-[0.06em] text-white transition hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] disabled:border-black/40 disabled:bg-[#2b2f39] disabled:text-white/60';

function formatEuro(value: number) {
  return new Intl.NumberFormat('en-US', {
    currency: 'EUR',
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(value);
}

function getLossRangePerCar(avgScore: number): [number, number, LeakageBand] {
  if (avgScore <= 0.45) {
    return [120, 250, 'low'];
  }
  if (avgScore <= 1) {
    return [260, 420, 'moderate'];
  }
  if (avgScore <= 1.5) {
    return [420, 680, 'high'];
  }
  return [700, 1100, 'critical'];
}

function getOptionLabel(questionId: QuizQuestionId, optionId?: string) {
  if (!optionId) {
    return 'Not selected';
  }
  const question = ALL_QUIZ_QUESTIONS.find((item) => item.id === questionId);
  const option = question?.options.find((item) => item.id === optionId);
  return option?.label || 'Not selected';
}

export function QuizPage() {
  useSeo({
    description:
      'Take the 60-second Profit Cars quiz to uncover hidden profit leaks in sourcing, listing quality, and margin tracking.',
    image: '/quizpage.png',
    keywords: [
      'car dealer quiz',
      'profit leak calculator',
      'deal sourcing quiz',
      'used car dealer margins',
      'car flipping profit quiz',
    ],
    path: '/quiz',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      description:
        'Interactive quiz for car dealers to estimate monthly losses caused by sourcing and process inefficiencies.',
      name: 'Profit Leak Quiz',
      url: '/quiz',
    },
    title: 'Profit Cars Quiz | Dealership Profit Leak Calculator',
    type: 'website',
  });

  const [stage, setStage] = useState<QuizStage>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [analyzingStep, setAnalyzingStep] = useState(0);
  const [manualInsightShown, setManualInsightShown] = useState(false);
  const [photoInsightShown, setPhotoInsightShown] = useState(false);
  const [insightType, setInsightType] = useState<InsightType>(null);
  const [error, setError] = useState<string | null>(null);

  const activeQuestions = useMemo(() => {
    const base = [...BASE_QUIZ_QUESTIONS];
    const dealSourceQuestionIndex = base.findIndex((question) => question.id === 'dealSource');

    if (dealSourceQuestionIndex === -1) {
      return base;
    }

    if (isMarketplaceSource(answers.dealSource)) {
      base.splice(dealSourceQuestionIndex + 1, 0, MARKETPLACE_FOLLOWUP_QUESTION);
    }

    return base;
  }, [answers.dealSource]);

  const currentQuestion = activeQuestions[questionIndex];
  const currentAnswer = answers[currentQuestion?.id];

  useEffect(() => {
    if (stage !== 'question') {
      return;
    }

    if (questionIndex > activeQuestions.length - 1) {
      setQuestionIndex(Math.max(activeQuestions.length - 1, 0));
    }
  }, [activeQuestions.length, questionIndex, stage]);

  useEffect(() => {
    if (stage !== 'analyzing') {
      return;
    }

    setAnalyzingStep(0);

    const timers = [
      window.setTimeout(() => setAnalyzingStep(1), 420),
      window.setTimeout(() => setAnalyzingStep(2), 980),
      window.setTimeout(() => setAnalyzingStep(3), 1540),
      window.setTimeout(() => setStage('community'), 2300),
    ];

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [stage]);

  const resultData = useMemo(() => {
    const scoredQuestionIds: QuizQuestionId[] = [
      'dealSource',
      'searchMethod',
      'speed',
      'listings',
      'profitTracking',
      'bottleneck',
    ];

    const totalScore = scoredQuestionIds.reduce((sum, questionId) => {
      const optionId = answers[questionId];
      if (!optionId) {
        return sum;
      }
      const question = ALL_QUIZ_QUESTIONS.find((item) => item.id === questionId);
      const option = question?.options.find((item) => item.id === optionId);
      return sum + (option?.score || 0);
    }, 0);

    const answeredScoredCount = scoredQuestionIds.reduce(
      (count, questionId) => (answers[questionId] ? count + 1 : count),
      0
    );

    const avgScore = answeredScoredCount > 0 ? totalScore / answeredScoredCount : 0;
    const volumeId = answers.volume || '1-5';
    const monthlyCars = VOLUME_TO_MONTHLY_CARS[volumeId] || 3;

    const [minLossPerCar, maxLossPerCar, leakageBand] = getLossRangePerCar(avgScore);
    const minMonthlyLoss = Math.round(monthlyCars * minLossPerCar);
    const maxMonthlyLoss = Math.round(monthlyCars * maxLossPerCar);

    const identity = answers.identity || 'dealership';
    const bottleneck = answers.bottleneck;

    const dealSource = answers.dealSource || 'website-marketplace';
    const searchMethod = answers.searchMethod || 'manual';

    const sourcingMessageBySource: Record<string, string> = {
      'facebook-marketplace':
        'You source via Facebook Marketplace, where speed and filtering discipline matter a lot. Slow scanning usually means missed underpriced cars.',
      'personal-network':
        'Network sourcing can work well, but relying only on contacts can hide profitable opportunities outside your immediate circle.',
      'website-marketplace':
        'You source from website marketplaces, where structured search and fast alerts create the biggest edge.',
    };

    const sourcingMessageByMethod: Record<string, string> = {
      'ai-automation':
        'You already use AI-based sourcing, which improves reaction speed and helps you catch better deals before competitors.',
      manual:
        'You rely on manual marketplace scanning, which slows reaction time and makes it easier to miss underpriced inventory.',
      mixed:
        'A mixed sourcing process gives coverage, but inconsistent execution can still leave profitable deals on the table.',
      'saved-alerts':
        'Saved searches and alerts help, but refining filters and response speed is where more margin is unlocked.',
    };

    const listingMessageMap: Record<string, string> = {
      'basic-editing':
        'Your listing process is partly manual, which can reduce perceived value and limit your ability to hold stronger prices.',
      'consistent-system':
        'Your listing quality looks structured. The upside now is consistency at scale across every vehicle and channel.',
      outsource:
        'Outsourcing helps quality, but lead time and cost can still hurt listing speed and net margins.',
      'quick-upload':
        'Your listing process reduces perceived value — costing you higher selling prices.',
    };

    const profitMessageMap: Record<string, string> = {
      accurate:
        'You are tracking margins well. The next step is combining cost visibility with sourcing speed to protect profit on every deal.',
      'no-tracking':
        'You don’t have full visibility on your margins — leading to hidden losses on deals.',
      'not-really':
        'You don’t have full visibility on your margins — leading to hidden losses on deals.',
      'rough-estimate':
        'Your margin view is approximate, which makes pricing and reinvestment decisions riskier than they need to be.',
    };

    const identityBridgeMap: Record<string, string> = {
      both:
        'Running both dealership operations and flipping requires one connected system so sourcing, listings, and margins stay aligned.',
      dealership:
        'As a dealership managing stock, consistency across sourcing, listings, and margin control is what protects your monthly cash flow.',
      flipper:
        'As a flipper, speed and margin precision make the difference between average deals and high-ROI inventory.',
    };

    const bottleneckMap: Record<string, string> = {
      'finding-deals':
        'Your top bottleneck is finding good deals, so faster deal detection and qualification should be your first unlock.',
      'managing-inventory':
        'Your top bottleneck is inventory management, so one clean operating system will reduce friction and mental overhead.',
      'selling-faster':
        'Your top bottleneck is selling speed, so stronger listing quality and faster launch cycles should be your immediate focus.',
      'tracking-profit':
        'Your top bottleneck is profit tracking, so live cost-to-margin visibility should be your first system upgrade.',
    };

    return {
      bottleneckMessage: bottleneck ? bottleneckMap[bottleneck] : null,
      identityLabel: getOptionLabel('identity', identity),
      leakageBand,
      listingMessage: listingMessageMap[answers.listings || 'quick-upload'],
      maxMonthlyLoss,
      minMonthlyLoss,
      monthlyCars,
      profitMessage: profitMessageMap[answers.profitTracking || 'no-tracking'],
      sourcingMessage: isMarketplaceSource(dealSource)
        ? sourcingMessageByMethod[searchMethod]
        : sourcingMessageBySource[dealSource],
      systemBridge: identityBridgeMap[identity],
      volumeLabel: getOptionLabel('volume', volumeId),
    };
  }, [answers]);

  const projectedMinRecovery = Math.round(resultData.minMonthlyLoss * 0.45);
  const projectedMaxRecovery = Math.round(resultData.maxMonthlyLoss * 0.68);

  const startQuiz = () => {
    setStage('question');
    setQuestionIndex(0);
    setAnswers({});
    setManualInsightShown(false);
    setPhotoInsightShown(false);
    setInsightType(null);
    setError(null);
  };

  const handleAnswerSelect = (questionId: QuizQuestionId, optionId: string) => {
    setAnswers((prev) => {
      const next: QuizAnswers = {
        ...prev,
        [questionId]: optionId,
      };

      if (questionId === 'dealSource' && !isMarketplaceSource(optionId)) {
        delete next.searchMethod;
      }

      return next;
    });

    if (questionId === 'dealSource' && !isMarketplaceSource(optionId)) {
      setManualInsightShown(false);
    }

    if (questionId === 'searchMethod' && optionId !== 'manual') {
      setManualInsightShown(false);
    }

    if (questionId === 'listings' && optionId === 'consistent-system') {
      setPhotoInsightShown(false);
    }

    setError(null);

    if (stage === 'question' && currentQuestion?.id === questionId) {
      window.setTimeout(() => {
        if (currentQuestion.id === 'searchMethod' && optionId === 'manual' && !manualInsightShown) {
          setManualInsightShown(true);
          setInsightType('sourcing');
          setStage('insight');
          return;
        }

        if (
          currentQuestion.id === 'listings' &&
          LISTINGS_INFO_TRIGGER_IDS.includes(
            optionId as (typeof LISTINGS_INFO_TRIGGER_IDS)[number]
          ) &&
          !photoInsightShown
        ) {
          setPhotoInsightShown(true);
          setInsightType('photo');
          setStage('insight');
          return;
        }

        if (questionIndex >= activeQuestions.length - 1) {
          setStage('analyzing');
          return;
        }

        setQuestionIndex((prev) => prev + 1);
      }, 80);
    }
  };

  const goToPreviousQuestion = () => {
    setError(null);

    if (questionIndex <= 0) {
      setStage('intro');
      return;
    }

    setQuestionIndex((prev) => prev - 1);
  };

  const skipOptionalQuestion = () => {
    if (!currentQuestion?.optional) {
      return;
    }
    setError(null);
    setStage('analyzing');
  };

  const continueFromInsight = () => {
    if (questionIndex >= activeQuestions.length - 1) {
      setStage('analyzing');
      return;
    }

    setQuestionIndex((prev) => prev + 1);
    setStage('question');
    setInsightType(null);
  };

  const backFromInsight = () => {
    setStage('question');
    setInsightType(null);
  };

  const continueFromCommunity = () => {
    setStage('projection');
  };

  const continueFromProjection = () => {
    setStage('result');
  };

  const restartQuiz = () => {
    setStage('intro');
    setQuestionIndex(0);
    setAnswers({});
    setError(null);
    setManualInsightShown(false);
    setPhotoInsightShown(false);
    setInsightType(null);
    setAnalyzingStep(0);
  };

  return (
    <main className="min-h-screen bg-[#ececec] py-20 text-[#111827] sm:py-24">
      <div className="mx-auto w-full max-w-[700px] px-6 lg:px-8">
        <div className="rounded-[34px] border border-black/10 bg-white p-6 shadow-[0_26px_54px_-38px_rgba(0,0,0,0.45)] sm:p-9">
          {stage === 'intro' && (
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#2f5b1b]">Profit Leak Quiz</p>
              <h1 className="mt-4 font-display text-4xl leading-[0.95] text-black sm:text-6xl">
                Find Out Where You’re Losing Money on Car Deals
              </h1>

              <div className="mt-7">
                <img
                  alt="Car dealership stock preview"
                  className="mx-auto h-auto w-full max-w-[360px] rounded-[18px] object-cover"
                  src={quizIntroImage}
                />
              </div>

              <ul className="mx-auto mt-7 flex w-fit max-w-full flex-col items-start gap-2 text-left">
                {introSubtextItems.map((item) => (
                  <li className="flex items-start gap-3 text-base font-semibold leading-relaxed text-slate-800 sm:text-lg" key={item}>
                    <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-black" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mx-auto mt-9 max-w-[420px]">
                <Button className={ctaClass} onClick={startQuiz} type="button">
                  Show my hidden profit leaks
                </Button>
              </div>

              <p className="mt-4 text-sm font-medium text-slate-500">Takes 60 seconds. No email required.</p>
            </div>
          )}

          {stage === 'question' && currentQuestion && (
            <div>
              <div className="mb-7">
                <div className="relative flex items-center">
                  <button
                    aria-label="Go back"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15 bg-white text-slate-700 transition hover:border-black/35 hover:text-black"
                    onClick={goToPreviousQuestion}
                    type="button"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>

                  <p className="absolute left-1/2 -translate-x-1/2 text-xs font-bold uppercase tracking-[0.14em] text-slate-600 sm:text-sm">
                    Question {questionIndex + 1} of {activeQuestions.length}
                  </p>

                  {currentQuestion.optional && (
                    <span className="ml-auto rounded-full border border-black/15 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                      Optional
                    </span>
                  )}
                </div>

                <div className="mx-auto mt-4 h-2 w-full max-w-[460px] overflow-hidden rounded-full bg-[#d7d9dd]">
                  <div
                    className="h-full rounded-full bg-[#08090c] transition-all duration-500"
                    style={{ width: `${((questionIndex + 1) / activeQuestions.length) * 100}%` }}
                  />
                </div>
              </div>

              <h2 className="font-display text-3xl leading-tight text-black sm:text-5xl">{currentQuestion.prompt}</h2>

              <div className="mt-6 grid gap-3">
                {currentQuestion.options.map((option) => {
                  const isSelected = currentAnswer === option.id;

                  return (
                    <button
                      className={cn(
                        'flex w-full items-center gap-4 rounded-[20px] border px-4 py-4 text-left transition sm:px-5 sm:py-5',
                        isSelected
                          ? 'border-[#53b12c] bg-[#eef8ea]'
                          : 'border-black/10 bg-[#f1f3f5] hover:border-black/20'
                      )}
                      key={option.id}
                      onClick={() => handleAnswerSelect(currentQuestion.id, option.id)}
                      type="button"
                    >
                      <span className="min-w-0 flex-1 text-lg font-semibold text-black sm:text-xl">
                        {option.emoji ? `${option.emoji} ${option.label}` : option.label}
                      </span>

                      <span
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-xl border',
                          isSelected ? 'border-black bg-black text-white' : 'border-black/35 bg-white text-transparent'
                        )}
                      >
                        <Check className="h-4 w-4" />
                      </span>
                    </button>
                  );
                })}
              </div>

              {error && (
                <p className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  {error}
                </p>
              )}

              {currentQuestion.optional && (
                <button
                  className="mt-4 text-sm font-semibold text-slate-500 underline-offset-4 transition hover:text-black hover:underline"
                  onClick={skipOptionalQuestion}
                  type="button"
                >
                  Skip this question
                </button>
              )}
            </div>
          )}

          {stage === 'insight' && (
            <div className="mx-auto max-w-3xl">
              <div className="flex items-center">
                <button
                  aria-label="Go back"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15 bg-white text-slate-700 transition hover:border-black/35 hover:text-black"
                  onClick={backFromInsight}
                  type="button"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-6 rounded-[20px] border border-black/10 bg-[#f4f4f5] p-6 sm:p-8">
                {insightType === 'photo' ? (
                  <>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">Quick insight</p>
                    <h2 className="mt-3 font-display text-3xl leading-tight text-black sm:text-5xl">
                      AI can make professional photos in 10 seconds.
                    </h2>

                    <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
                      If your current listing workflow is manual, basic, or outsourced, better visuals can immediately
                      increase clicks and buyer trust.
                    </p>

                    <div className="mt-6 overflow-hidden rounded-[16px] border border-black/10 bg-white p-3">
                      <div className="relative aspect-[16/10] overflow-hidden rounded-[12px] border border-black/10 bg-black">
                        <ReactCompareSlider
                          className="h-full w-full"
                          defaultPosition={54}
                          itemOne={
                            <ReactCompareSliderImage
                              alt="Before AI photo enhancement"
                              src={aiBeforeImage}
                              style={{ filter: 'brightness(0.8) saturate(0.72)', height: '100%', objectFit: 'cover', width: '100%' }}
                            />
                          }
                          itemTwo={
                            <ReactCompareSliderImage
                              alt="After AI photo enhancement"
                              src={aiAfterImage}
                              style={{ height: '100%', objectFit: 'cover', width: '100%' }}
                            />
                          }
                          onlyHandleDraggable={false}
                          transition="0.24s cubic-bezier(0.22,1,0.36,1)"
                        />

                        <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/72 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-white">
                          Before
                        </div>
                        <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-[#53b12c]/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-black">
                          After
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">Did you know</p>
                    <h2 className="mt-3 font-display text-3xl leading-tight text-black sm:text-5xl">
                      Car dealers use AI to source the best deals while they sleep.
                    </h2>

                    <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
                      AI scans marketplaces continuously, identifies strong opportunities, and sends instant
                      notifications when a deal matches your criteria.
                    </p>
                    <p className="mt-3 text-base leading-relaxed text-slate-700 sm:text-lg">
                      Sometimes acting fast is half of the profit.
                    </p>

                    <div className="mt-6 overflow-hidden rounded-[16px] border border-black/10 bg-white">
                      <img
                        alt="AI marketplace search workflow preview"
                        className="h-auto w-full object-cover"
                        loading="lazy"
                        src="/quiz-ai-search.png"
                      />
                    </div>
                  </>
                )}

                <div className="mt-8 w-full">
                  <Button className={ctaClass} onClick={continueFromInsight} type="button">
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stage === 'analyzing' && (
            <div className="mx-auto max-w-2xl py-8 text-center">
              <h2 className="font-display text-4xl leading-tight text-black sm:text-5xl">Analyzing your dealership...</h2>

              <div className="mx-auto mt-8 max-w-xl space-y-4 text-left">
                {ANALYZING_LINES.map((line, index) => {
                  const active = analyzingStep > index;
                  return (
                    <div
                      className={cn(
                        'flex items-center gap-3 rounded-[14px] border px-4 py-3 text-base font-semibold transition',
                        active
                          ? 'border-[#53b12c]/45 bg-[#eef8ea] text-[#234b14]'
                          : 'border-black/10 bg-[#f4f4f5] text-slate-500'
                      )}
                      key={line}
                    >
                      <span
                        aria-hidden="true"
                        className={cn('h-2.5 w-2.5 rounded-full', active ? 'bg-[#53b12c]' : 'bg-slate-300')}
                      />
                      {line}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {stage === 'community' && (
            <div className="mx-auto max-w-3xl">
              <div className="relative mx-auto h-[270px] w-full max-w-[620px] sm:h-[340px]">
                <div
                  className="absolute inset-0 rounded-[22px] border border-black/10 bg-[#f3f4f6]"
                  style={{
                    backgroundImage: "url('/europe-map.svg')",
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '120%',
                    filter: 'grayscale(1)',
                  }}
                />
                <div className="absolute inset-0 rounded-[22px] bg-white/55" />

                {communityPins.map((pin) => (
                  <div
                    className="absolute -translate-x-1/2"
                    key={`${pin.left}-${pin.top}`}
                    style={{ left: pin.left, top: pin.top }}
                  >
                    <div className="relative flex flex-col items-center">
                      <span className="h-0 w-0 border-l-[14px] border-r-[14px] border-t-[24px] border-l-transparent border-r-transparent border-t-[#58ab78]" />
                      <span className="absolute -top-11 inline-flex h-11 w-11 items-center justify-center rounded-full border-4 border-[#58ab78] bg-white shadow-[0_6px_16px_-10px_rgba(0,0,0,0.55)]">
                        <img
                          alt="Dealer community member"
                          className="h-8 w-8 rounded-full object-cover"
                          loading="lazy"
                          src={pin.src}
                        />
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 text-center">
                <h2 className="font-display text-4xl leading-tight text-black sm:text-5xl">Join over 2,500,000 people</h2>
                <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
                  Become part of a growing worldwide community and achieve your goals with us.
                </p>
              </div>

              <div className="mx-auto mt-9 max-w-[420px]">
                <Button className={ctaClass} onClick={continueFromCommunity} type="button">
                  See my income projection
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {stage === 'projection' && (
            <div className="mx-auto max-w-3xl">
              <div className="rounded-[20px] border border-black/10 bg-[#f4f4f5] p-6 sm:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">Income projection</p>
                <h2 className="mt-3 font-display text-3xl leading-tight text-black sm:text-5xl">
                  Your <span className="text-[#4f9b73]">4-week profit recovery</span> forecast is ready.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-700 sm:text-lg">
                  Based on your answers, optimizing your sourcing, listing quality, and margin control could recover{' '}
                  <span className="font-bold text-black">
                    {formatEuro(projectedMinRecovery)}–{formatEuro(projectedMaxRecovery)}
                  </span>{' '}
                  per month.
                </p>

                <div className="mt-8 rounded-[16px] border border-black/10 bg-white p-3 sm:p-5">
                  <svg
                    aria-label="4-week income projection chart"
                    className="h-auto w-full"
                    role="img"
                    viewBox="0 0 640 330"
                  >
                    <defs>
                      <linearGradient id="projectionLine" x1="0%" x2="100%" y1="0%" y2="0%">
                        <stop offset="0%" stopColor="#df6d5a" />
                        <stop offset="52%" stopColor="#e7cf4c" />
                        <stop offset="100%" stopColor="#7ebd4f" />
                      </linearGradient>
                      <linearGradient id="projectionFill" x1="0%" x2="100%" y1="0%" y2="0%">
                        <stop offset="0%" stopColor="#e2826c" stopOpacity="0.2" />
                        <stop offset="52%" stopColor="#e7d96f" stopOpacity="0.16" />
                        <stop offset="100%" stopColor="#8bc76a" stopOpacity="0.22" />
                      </linearGradient>
                    </defs>

                    <rect fill="#ffffff" height="330" rx="12" width="640" x="0" y="0" />

                    {[50, 95, 140, 185, 230].map((y) => (
                      <line key={y} stroke="#d4d7de" strokeDasharray="8 8" strokeWidth="2" x1="48" x2="604" y1={y} y2={y} />
                    ))}

                    {[120, 250, 380, 520].map((x) => (
                      <line key={x} stroke="#c5c8d2" strokeWidth="2" x1={x} x2={x} y1="50" y2="268" />
                    ))}

                    <path
                      d="M48 230 C108 230, 155 225, 250 165 C304 126, 338 74, 380 78 C422 82, 475 53, 520 52 L520 268 L48 268 Z"
                      fill="url(#projectionFill)"
                    />
                    <path
                      d="M48 230 C108 230, 155 225, 250 165 C304 126, 338 74, 380 78 C422 82, 475 53, 520 52"
                      fill="none"
                      stroke="url(#projectionLine)"
                      strokeLinecap="round"
                      strokeWidth="6"
                    />

                    <circle cx="120" cy="226" fill="#df6d5a" r="13" stroke="#ffffff" strokeWidth="7" />
                    <circle cx="250" cy="165" fill="#de9d59" r="13" stroke="#ffffff" strokeWidth="7" />
                    <circle cx="380" cy="78" fill="#e6d44a" r="13" stroke="#ffffff" strokeWidth="7" />
                    <circle cx="520" cy="52" fill="#7ebd4f" r="13" stroke="#ffffff" strokeWidth="7" />

                    <polygon fill="#7ebd4f" points="548,52 520,35 520,69" />

                    <g>
                      <rect fill="#d47966" height="34" rx="8" width="90" x="74" y="168" />
                      <polygon fill="#d47966" points="117,202 129,214 141,202" />
                      <text fill="#ffffff" fontSize="15" fontWeight="700" textAnchor="middle" x="119" y="190">
                        Today
                      </text>
                    </g>

                    <g>
                      <rect fill="#7ebd4f" height="54" rx="10" width="146" x="448" y="84" />
                      <polygon fill="#7ebd4f" points="518,84 529,72 540,84" />
                      <text fill="#ffffff" fontSize="14" fontWeight="700" textAnchor="middle" x="521" y="108">
                        After using
                      </text>
                      <text fill="#ffffff" fontSize="14" fontWeight="700" textAnchor="middle" x="521" y="128">
                        Profit Cars
                      </text>
                    </g>

                    <line stroke="#b9bcc6" strokeWidth="2.5" x1="48" x2="604" y1="268" y2="268" />

                    {[['WEEK 1', 120], ['WEEK 2', 250], ['WEEK 3', 380], ['WEEK 4', 520]].map(([label, x]) => (
                      <g key={label}>
                        <circle cx={Number(x)} cy="268" fill="#aeb1be" r="7" />
                        <text fill="#8f93a3" fontSize="12" fontWeight="700" textAnchor="middle" x={Number(x)} y="296">
                          {label}
                        </text>
                      </g>
                    ))}
                  </svg>

                  <p className="mt-3 text-center text-xs leading-relaxed text-slate-500 sm:text-sm">
                    *Based on quiz answers and dealer benchmarks. Final results vary by market, speed, and execution.
                  </p>
                </div>

                <div className="mx-auto mt-8 max-w-[420px]">
                  <Button className={ctaClass} onClick={continueFromProjection} type="button">
                    Continue to offer
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stage === 'result' && (
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#2f5b1b]">Your Result</p>
              <h2 className="mt-3 font-display text-4xl leading-[0.95] text-black sm:text-6xl">
                You’re likely losing {formatEuro(resultData.minMonthlyLoss)}–{formatEuro(resultData.maxMonthlyLoss)} per
                month in hidden inefficiencies
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-lg">
                Based on your answers, here’s where your dealership is leaking profit:
              </p>

              <div className="mt-8 grid gap-4">
                <article className="rounded-[18px] border border-black/10 bg-[#f4f4f5] p-5">
                  <h3 className="text-xl font-extrabold text-black">🔎 Sourcing</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-700">{resultData.sourcingMessage}</p>
                </article>

                <article className="rounded-[18px] border border-black/10 bg-[#f4f4f5] p-5">
                  <h3 className="text-xl font-extrabold text-black">📸 Listings</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-700">{resultData.listingMessage}</p>
                </article>

                <article className="rounded-[18px] border border-black/10 bg-[#f4f4f5] p-5">
                  <h3 className="text-xl font-extrabold text-black">💸 Profit Tracking</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-700">{resultData.profitMessage}</p>
                </article>
              </div>

              <div className="mt-8 rounded-[20px] border border-[#53b12c]/35 bg-[#eef8ea] p-5">
                <p className="text-base leading-relaxed text-[#234b14]">{resultData.systemBridge}</p>
                {resultData.bottleneckMessage && (
                  <p className="mt-3 text-base leading-relaxed text-[#234b14]">{resultData.bottleneckMessage}</p>
                )}
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#2f5b1b]">
                  Segment: {resultData.identityLabel} • Volume: {resultData.volumeLabel} • Leakage level:{' '}
                  {resultData.leakageBand}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link className="w-full sm:flex-1" to="/get">
                  <Button className={ctaClass}>
                    Get access to Profit Cars
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <button
                  className="h-14 rounded-[10px] border border-black/20 px-6 text-sm font-bold uppercase tracking-[0.08em] text-slate-700 transition hover:border-black/40 hover:text-black"
                  onClick={restartQuiz}
                  type="button"
                >
                  Retake quiz
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
